/**
 * Return keyboard focus to the control that opened a dialog once it closes (WCAG 2.4.3).
 *
 * angular-modal-service blurs `document.activeElement` when a modal opens and never
 * restores it, so focus falls back to <body> on close. `modalFocusReturnDecorator`
 * wraps `ModalService.showModal` so every dialog in the app gets this behaviour.
 */

const DROPDOWN_CONTAINER = '.dropdown, .dropup, .dropleft, .dropright, .btn-group, .ellipsis__dropdown';
const TABBABLE = 'a[href], button, input, select, textarea, [tabindex]';
const DROPDOWN_TOGGLE = '[data-toggle="dropdown"], .dropdown-toggle';

const isFocusable = (el: HTMLElement | null): boolean => {
	if (!el || !document.body.contains(el) || typeof el.focus !== 'function') {
		return false;
	}
	if ((el as HTMLButtonElement).disabled) {
		return false;
	}
	// offsetParent is null for display:none (and its descendants)
	return el.offsetParent !== null || getComputedStyle(el).position === 'fixed';
};

const focusElement = (el: HTMLElement | null): boolean => {
	if (!isFocusable(el)) {
		return false;
	}
	el.focus();
	return document.activeElement === el;
};

/**
 * Trigger elements are often items in an ellipsis/dropdown menu that is hidden by
 * the time the dialog closes. In that case fall back to the menu's toggle button.
 */
const getFallbackTarget = (trigger: HTMLElement): HTMLElement | null => {
	const container = trigger.closest ? trigger.closest(DROPDOWN_CONTAINER) : null;
	if (container) {
		const toggle = container.querySelector(DROPDOWN_TOGGLE) as HTMLElement;
		if (isFocusable(toggle)) {
			return toggle;
		}
	}

	// The trigger may have been re-rendered (ng-repeat/ng-if); look it up by id.
	if (trigger.id) {
		const replacement = document.getElementById(trigger.id);
		if (replacement && replacement !== trigger && isFocusable(replacement)) {
			return replacement;
		}
	}
	return null;
};

/**
 * The next element in Tab order after `el`. If `el` is inside a dropdown menu, the whole
 * dropdown (toggle + hidden items) is skipped so focus lands on the control after the menu.
 * Elements inside dialogs are ignored.
 */
export const getNextFocusable = (el: HTMLElement | null): HTMLElement | null => {
	if (!el || !document.body.contains(el)) return null;

	const anchor = ((el.closest && el.closest(DROPDOWN_CONTAINER)) as HTMLElement) || el;
	const candidates = document.querySelectorAll(TABBABLE);

	for (let i = 0; i < candidates.length; i++) {
		const candidate = candidates[i] as HTMLElement;
		if (anchor.contains(candidate)) continue;
		if (!(anchor.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
		if (candidate.getAttribute('tabindex') === '-1') continue;
		if (candidate.closest('.modal')) continue;
		if (isFocusable(candidate)) return candidate;
	}
	return null;
};

/** Last resort: put focus at the start of the main content instead of losing it. */
const focusMainContent = (): void => {
	const main = (document.querySelector('main, [role="main"], #main-content') as HTMLElement) || document.body;
	if (main !== document.body && !main.hasAttribute('tabindex')) {
		main.setAttribute('tabindex', '-1');
	}
	main.focus();
};

export const returnFocus = (trigger: HTMLElement | null, modalService?: any): void => {
	// Another dialog opened in the meantime (e.g. a confirm step); let it keep focus.
	if (modalService && modalService.openModals && modalService.openModals.length > 0) {
		return;
	}

	const active = document.activeElement as HTMLElement;
	// The dialog's own close handler (or the workflow) already placed focus somewhere useful.
	// (Hidden elements don't count: some browsers keep activeElement on a now-hidden control.)
	if (active && active !== document.body && active !== trigger && isFocusable(active)) {
		return;
	}

	if (trigger && trigger !== document.body) {
		if (focusElement(trigger) || focusElement(getFallbackTarget(trigger))) {
			return;
		}
	}
	focusMainContent();
};

export const modalFocusReturnDecorator = [
	'$delegate',
	'$timeout',
	($delegate: any, $timeout: ng.ITimeoutService) => {
		const originalShowModal = $delegate.showModal;

		$delegate.showModal = function (...args: any[]) {
			// Captured before angular-modal-service blurs the active element
			const trigger = document.activeElement as HTMLElement;

			return originalShowModal.apply(this, args).then((modal: any) => {
				if (modal && modal.closed && typeof modal.closed.then === 'function') {
					// `closed` resolves after the modal element has been removed from the DOM
					modal.closed.then(() => {
						$timeout(() => returnFocus(trigger, $delegate), 0, false);
					});
				}
				return modal;
			});
		};

		return $delegate;
	},
];
